/**
 * cdn-domain-cache-tab.tsx — Cache configuration tab for a CDN domain (act-as).
 *
 * Mirrors Partner's CacheTab.tsx: Behavior, TTLs, Cache Key, Vary, Per-Status TTL.
 * Mutations route through ActAsMutationConfirm — requires an operator reason.
 *
 * Endpoints: GET/PUT/DELETE /api/v3/cdn/domains/:id/cache
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Clock } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Switch,
  Skeleton,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNCache,
  useDeleteActAsCDNCache,
  actAsCDNCacheKeys,
  type CacheConfig,
} from '@/api/act-as-cdn-cache';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

/* ── Form model ──────────────────────────────────────────────────────────── */

interface CacheForm {
  enabled: boolean;
  bypassCache: boolean;
  ignoreClientNoCache: boolean;
  defaultTTL: string;
  maxTTL: string;
  edgeTTL: string;
  browserTTL: string;
  staleWhileRevalidate: string;
  staleIfError: string;
  customCacheKey: boolean;
  queryStringStrategy: string;
  queryParams: string;
  sortQueryParams: boolean;
  cachingHeaders: string;
  cachingCookies: string;
  detectDeviceType: boolean;
  detectGeoIP: boolean;
  detectLanguage: boolean;
  customVaryHandling: boolean;
  normalizeAcceptEncoding: boolean;
  varyAllowlist: string;
  statusTTL: { status: string; ttl: string }[];
}

const STATUS_QUICK_ADD = ['400', '401', '403', '404', '429', '500', '502', '503', '504'];

const DEFAULTS: CacheForm = {
  enabled: true,
  bypassCache: false,
  ignoreClientNoCache: true,
  defaultTTL: '3600',
  maxTTL: '2592000',
  edgeTTL: '0',
  browserTTL: '0',
  staleWhileRevalidate: '0',
  staleIfError: '0',
  customCacheKey: false,
  queryStringStrategy: 'all',
  queryParams: '',
  sortQueryParams: false,
  cachingHeaders: '',
  cachingCookies: '',
  detectDeviceType: false,
  detectGeoIP: false,
  detectLanguage: false,
  customVaryHandling: false,
  normalizeAcceptEncoding: true,
  varyAllowlist: '',
  statusTTL: [],
};

const csv = (arr: unknown): string => (Array.isArray(arr) ? (arr as string[]).join(', ') : '');
const splitCsv = (s: string): string[] => s.split(',').map((x) => x.trim()).filter(Boolean);

function hydrate(cfg: CacheConfig): CacheForm {
  const key = cfg.cache_key ?? null;
  const vary = cfg.vary ?? null;
  return {
    enabled: cfg.enabled ?? true,
    bypassCache: cfg.bypass_cache ?? false,
    ignoreClientNoCache: cfg.ignore_client_no_cache ?? true,
    defaultTTL: String(cfg.default_ttl_secs ?? 3600),
    maxTTL: String(cfg.max_ttl_secs ?? 2592000),
    edgeTTL: String(cfg.edge_ttl_secs ?? 0),
    browserTTL: String(cfg.browser_ttl_secs ?? 0),
    staleWhileRevalidate: String(cfg.stale_while_revalidate_secs ?? 0),
    staleIfError: String(cfg.stale_if_error_secs ?? 0),
    customCacheKey: !!key,
    queryStringStrategy: key?.query_strategy ?? 'all',
    queryParams: csv(key?.query_params),
    sortQueryParams: key?.query_sort ?? false,
    cachingHeaders: csv(key?.header_include),
    cachingCookies: csv(key?.cookie_include),
    detectDeviceType: key?.device_type ?? false,
    detectGeoIP: key?.geo ?? false,
    detectLanguage: key?.lang ?? false,
    customVaryHandling: !!vary,
    normalizeAcceptEncoding: vary?.normalize_accept_encoding ?? true,
    varyAllowlist: csv(vary?.allowlist),
    statusTTL: Object.entries(cfg.status_ttl ?? {}).map(([status, ttl]) => ({
      status,
      ttl: String(ttl),
    })),
  };
}

function toPayload(v: CacheForm): CacheConfig {
  return {
    enabled: v.enabled,
    bypass_cache: v.bypassCache,
    ignore_client_no_cache: v.ignoreClientNoCache,
    default_ttl_secs: Number(v.defaultTTL) || 0,
    max_ttl_secs: Number(v.maxTTL) || 0,
    edge_ttl_secs: Number(v.edgeTTL) || 0,
    browser_ttl_secs: Number(v.browserTTL) || 0,
    stale_while_revalidate_secs: Number(v.staleWhileRevalidate) || 0,
    stale_if_error_secs: Number(v.staleIfError) || 0,
    cache_key: v.customCacheKey
      ? {
          query_strategy: v.queryStringStrategy,
          query_params: splitCsv(v.queryParams),
          query_sort: v.sortQueryParams,
          header_include: splitCsv(v.cachingHeaders),
          cookie_include: splitCsv(v.cachingCookies),
          device_type: v.detectDeviceType,
          geo: v.detectGeoIP,
          lang: v.detectLanguage,
        }
      : null,
    vary: v.customVaryHandling
      ? {
          enabled: true,
          allowlist: splitCsv(v.varyAllowlist),
          normalize_accept_encoding: v.normalizeAcceptEncoding,
        }
      : null,
    status_ttl: v.statusTTL.reduce<Record<string, number>>((acc, { status, ttl }) => {
      const code = status.trim();
      if (code) acc[code] = Number(ttl) || 0;
      return acc;
    }, {}),
  };
}

/* ── Component ───────────────────────────────────────────────────────────── */

interface CdnDomainCacheTabProps {
  domainId: string;
}

export function CdnDomainCacheTab({ domainId }: CdnDomainCacheTabProps) {
  const { t } = useTranslation();
  const cacheQ = useActAsCDNCache(domainId);
  const isConfigured = !!cacheQ.data;

  const [form, setForm] = useState<CacheForm>(DEFAULTS);
  const [snapshot, setSnapshot] = useState<CacheForm>(DEFAULTS);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    const next = cacheQ.data ? hydrate(cacheQ.data) : DEFAULTS;
    setForm(next);
    setSnapshot(next);
  }, [cacheQ.data]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(snapshot),
    [form, snapshot],
  );

  const set = <K extends keyof CacheForm>(k: K, val: CacheForm[K]) =>
    setForm((f) => ({ ...f, [k]: val }));

  // Per-status TTL helpers
  const addStatusRow = (status = '') =>
    setForm((f) =>
      f.statusTTL.some((r) => r.status === status && status)
        ? f
        : { ...f, statusTTL: [...f.statusTTL, { status, ttl: '0' }] },
    );
  const setStatusRow = (i: number, key: 'status' | 'ttl', val: string) =>
    setForm((f) => ({
      ...f,
      statusTTL: f.statusTTL.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)),
    }));
  const removeStatusRow = (i: number) =>
    setForm((f) => ({ ...f, statusTTL: f.statusTTL.filter((_, idx) => idx !== i) }));
  const usedStatuses = new Set(form.statusTTL.map((r) => r.status));

  const deleteM = useDeleteActAsCDNCache(domainId);

  if (cacheQ.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (cacheQ.isError) {
    return (
      <TabError
        title={t('cdn_domain.cache_tab.load_error', 'Failed to load cache configuration')}
        onRetry={() => cacheQ.refetch()}
      />
    );
  }

  const payload = toPayload(form);
  const saveSummary = t(
    'cdn_domain.cache_tab.save_summary',
    'Update cache configuration — TTL values, cache key strategy, and Vary settings.',
  );
  const deleteSummary = t(
    'cdn_domain.cache_tab.delete_summary',
    'Remove the custom cache configuration. The edge will fall back to provider defaults.',
  );

  return (
    <Section
      title={t('cdn_domain.cache_tab.title', 'Cache')}
      description={t('cdn_domain.cache_tab.description', 'Edge cache TTLs, key strategy, and behavior for this domain.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* ── Behavior ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.cache_tab.section_behavior', 'Behavior')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.cache_tab.section_behavior_desc', 'How the edge decides what to cache.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title={t('cdn_domain.cache_tab.field_enabled', 'Caching enabled')}
              description={t('cdn_domain.cache_tab.field_enabled_desc', 'Serve responses from the edge cache.')}
              control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />}
            />
            <SettingRow
              title={t('cdn_domain.cache_tab.field_bypass', 'Bypass cache')}
              description={t('cdn_domain.cache_tab.field_bypass_desc', 'Always fetch from the origin (no edge caching).')}
              control={<Switch checked={form.bypassCache} onCheckedChange={(v) => set('bypassCache', v)} />}
            />
            <SettingRow
              title={t('cdn_domain.cache_tab.field_ignore_no_cache', 'Ignore client no-cache')}
              description={t('cdn_domain.cache_tab.field_ignore_no_cache_desc', 'Ignore Cache-Control: no-cache from the client.')}
              control={
                <Switch
                  checked={form.ignoreClientNoCache}
                  onCheckedChange={(v) => set('ignoreClientNoCache', v)}
                />
              }
            />
          </CardBody>
        </Card>

        {/* ── TTLs ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.cache_tab.section_ttl', 'TTLs')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.cache_tab.section_ttl_desc', 'Time-to-live, in seconds, for cached responses.')}
            </CardDescription>
          </CardHeader>
          <CardBody>
            <div
              style={{
                display: 'grid',
                gap: 'var(--vn-space-4)',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              }}
            >
              {(
                [
                  ['defaultTTL', t('cdn_domain.cache_tab.field_default_ttl', 'Default TTL')],
                  ['maxTTL', t('cdn_domain.cache_tab.field_max_ttl', 'Max TTL')],
                  ['edgeTTL', t('cdn_domain.cache_tab.field_edge_ttl', 'Edge TTL')],
                  ['browserTTL', t('cdn_domain.cache_tab.field_browser_ttl', 'Browser TTL')],
                  ['staleWhileRevalidate', t('cdn_domain.cache_tab.field_swr', 'Stale while revalidate')],
                  ['staleIfError', t('cdn_domain.cache_tab.field_sie', 'Stale if error')],
                ] as [keyof CacheForm, string][]
              ).map(([k, label]) => (
                <Field key={k} label={label}>
                  {(props) => (
                    <NumericInput
                      {...props}
                      value={String(form[k])}
                      onChange={(e) => set(k, e.target.value as CacheForm[typeof k])}
                    />
                  )}
                </Field>
              ))}
            </div>
          </CardBody>
        </Card>

        {/* ── Cache key ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.cache_tab.section_cache_key', 'Cache key')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.cache_tab.section_cache_key_desc', 'What makes two requests resolve to the same cached object.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title={t('cdn_domain.cache_tab.field_custom_key', 'Custom cache key')}
              description={t('cdn_domain.cache_tab.field_custom_key_desc', 'Override the default cache key strategy.')}
              control={
                <Switch
                  checked={form.customCacheKey}
                  onCheckedChange={(v) => set('customCacheKey', v)}
                />
              }
            />
            {form.customCacheKey && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)', marginTop: 'var(--vn-space-3)' }}>
                <Field label={t('cdn_domain.cache_tab.field_qs_strategy', 'Query string strategy')}>
                  {(props) => (
                    <Select
                      value={form.queryStringStrategy}
                      onValueChange={(v) => set('queryStringStrategy', v)}
                    >
                      <SelectTrigger {...props}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('cdn_domain.cache_tab.qs_all', 'Include all query params')}</SelectItem>
                        <SelectItem value="none">{t('cdn_domain.cache_tab.qs_none', 'Ignore all query params')}</SelectItem>
                        <SelectItem value="include">{t('cdn_domain.cache_tab.qs_include', 'Include listed params')}</SelectItem>
                        <SelectItem value="exclude">{t('cdn_domain.cache_tab.qs_exclude', 'Exclude listed params')}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                <Field
                  label={t('cdn_domain.cache_tab.field_query_params', 'Query params')}
                  description={t('cdn_domain.cache_tab.field_query_params_desc', 'Comma-separated, used by include/exclude strategies.')}
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={form.queryParams}
                      placeholder="utm_source, ref"
                      onChange={(e) => set('queryParams', e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label={t('cdn_domain.cache_tab.field_headers_key', 'Headers in key')}
                  description={t('cdn_domain.cache_tab.field_headers_key_desc', 'Comma-separated header names.')}
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={form.cachingHeaders}
                      placeholder="Accept, X-Region"
                      onChange={(e) => set('cachingHeaders', e.target.value)}
                    />
                  )}
                </Field>
                <Field
                  label={t('cdn_domain.cache_tab.field_cookies_key', 'Cookies in key')}
                  description={t('cdn_domain.cache_tab.field_cookies_key_desc', 'Comma-separated cookie names.')}
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={form.cachingCookies}
                      placeholder="session, locale"
                      onChange={(e) => set('cachingCookies', e.target.value)}
                    />
                  )}
                </Field>
                <SettingRow
                  title={t('cdn_domain.cache_tab.field_sort_qs', 'Sort query params')}
                  description={t('cdn_domain.cache_tab.field_sort_qs_desc', 'Normalize param order so ?a=1&b=2 and ?b=2&a=1 share a cache entry.')}
                  control={
                    <Switch
                      checked={form.sortQueryParams}
                      onCheckedChange={(v) => set('sortQueryParams', v)}
                    />
                  }
                />
                <SettingRow
                  title={t('cdn_domain.cache_tab.field_device_type', 'Detect device type')}
                  control={
                    <Switch
                      checked={form.detectDeviceType}
                      onCheckedChange={(v) => set('detectDeviceType', v)}
                    />
                  }
                />
                <SettingRow
                  title={t('cdn_domain.cache_tab.field_geoip', 'Detect GeoIP')}
                  control={
                    <Switch
                      checked={form.detectGeoIP}
                      onCheckedChange={(v) => set('detectGeoIP', v)}
                    />
                  }
                />
                <SettingRow
                  title={t('cdn_domain.cache_tab.field_language', 'Detect language')}
                  control={
                    <Switch
                      checked={form.detectLanguage}
                      onCheckedChange={(v) => set('detectLanguage', v)}
                    />
                  }
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* ── Vary ── */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.cache_tab.section_vary', 'Vary')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.cache_tab.section_vary_desc', 'Control how the Vary response header affects caching.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title={t('cdn_domain.cache_tab.field_custom_vary', 'Custom Vary handling')}
              control={
                <Switch
                  checked={form.customVaryHandling}
                  onCheckedChange={(v) => set('customVaryHandling', v)}
                />
              }
            />
            {form.customVaryHandling && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)', marginTop: 'var(--vn-space-3)' }}>
                <SettingRow
                  title={t('cdn_domain.cache_tab.field_normalize_encoding', 'Normalize Accept-Encoding')}
                  description={t('cdn_domain.cache_tab.field_normalize_encoding_desc', 'Collapse encodings to gzip / br / identity.')}
                  control={
                    <Switch
                      checked={form.normalizeAcceptEncoding}
                      onCheckedChange={(v) => set('normalizeAcceptEncoding', v)}
                    />
                  }
                />
                <Field
                  label={t('cdn_domain.cache_tab.field_vary_allowlist', 'Vary allowlist')}
                  description={t('cdn_domain.cache_tab.field_vary_allowlist_desc', 'Comma-separated Vary header values to honor.')}
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={form.varyAllowlist}
                      placeholder="Accept-Encoding, Accept-Language"
                      onChange={(e) => set('varyAllowlist', e.target.value)}
                    />
                  )}
                </Field>
              </div>
            )}
          </CardBody>
        </Card>

        {/* ── Per-Status TTL ── */}
        <Card>
          <CardHeader
            actions={
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={() => addStatusRow()}
              >
                {t('cdn_domain.cache_tab.add_status_ttl', 'Add')}
              </Button>
            }
          >
            <CardTitle>{t('cdn_domain.cache_tab.section_status_ttl', 'Per-Status TTL')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.cache_tab.section_status_ttl_desc', 'Override cache TTL (seconds) for specific HTTP status codes. 0 = no-store.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                {t('cdn_domain.cache_tab.quick_add', 'Quick add:')}
              </span>
              {STATUS_QUICK_ADD.map((code) => (
                <Badge
                  key={code}
                  tone={usedStatuses.has(code) ? 'neutral' : 'info'}
                  appearance="subtle"
                  size="sm"
                  style={{
                    cursor: usedStatuses.has(code) ? 'default' : 'pointer',
                    opacity: usedStatuses.has(code) ? 0.5 : 1,
                  }}
                  onClick={() => !usedStatuses.has(code) && addStatusRow(code)}
                >
                  {code}
                </Badge>
              ))}
            </div>
            {form.statusTTL.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<Clock size={20} />}
                title={t('cdn_domain.cache_tab.no_status_ttl', 'No per-status TTL overrides yet')}
                description={t('cdn_domain.cache_tab.no_status_ttl_desc', 'Status codes fall back to the global TTLs above.')}
              />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {form.statusTTL.map((row, i) => (
                  <div
                    key={i}
                    style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}
                  >
                    <Input
                      aria-label={t('cdn_domain.cache_tab.status_code_label', 'Status code')}
                      value={row.status}
                      placeholder="404"
                      onChange={(e) =>
                        setStatusRow(i, 'status', e.target.value.replace(/[^0-9]/g, '').slice(0, 3))
                      }
                      style={{ maxWidth: 110 }}
                    />
                    <span style={{ color: 'var(--vn-fg-muted)' }}>=</span>
                    <NumericInput
                      aria-label={t('cdn_domain.cache_tab.ttl_seconds_label', 'TTL seconds')}
                      value={row.ttl}
                      onChange={(e) => setStatusRow(i, 'ttl', e.target.value)}
                      style={{ maxWidth: 160 }}
                    />
                    <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
                      {t('cdn_domain.cache_tab.secs', 'secs')}
                    </span>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={t('cdn_domain.cache_tab.remove_status_ttl', 'Remove')}
                      onClick={() => removeStatusRow(i)}
                    >
                      <X size={15} />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          start={
            isConfigured ? (
              <Button
                variant="danger"
                onClick={() => setDeleteConfirmOpen(true)}
              >
                {t('cdn_domain.cache_tab.action_remove', 'Remove')}
              </Button>
            ) : undefined
          }
          end={
            <>
              {isConfigured && (
                <Button
                  variant="secondary"
                  disabled={!dirty}
                  onClick={() => setForm(snapshot)}
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              )}
              <Button
                disabled={isConfigured && !dirty}
                onClick={() => setConfirmOpen(true)}
              >
                {isConfigured
                  ? t('cdn_domain.cache_tab.action_save', 'Save')
                  : t('cdn_domain.cache_tab.action_create', 'Create')}
              </Button>
            </>
          }
        />
      </div>

      {/* ── Save confirmation ── */}
      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={
          isConfigured
            ? t('cdn_domain.cache_tab.confirm_save_title', 'Save cache configuration')
            : t('cdn_domain.cache_tab.confirm_create_title', 'Create cache configuration')
        }
        summary={saveSummary}
        queryKeysToInvalidate={[actAsCDNCacheKeys.cache(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/cache`, payload)}
        onSuccess={() => setSnapshot(form)}
      />

      {/* ── Delete confirmation ── */}
      <ActAsMutationConfirm
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('cdn_domain.cache_tab.confirm_delete_title', 'Remove cache configuration')}
        summary={deleteSummary}
        queryKeysToInvalidate={[actAsCDNCacheKeys.cache(domainId)]}
        mutationFn={() => deleteM.mutateAsync()}
        onSuccess={() => {
          setForm(DEFAULTS);
          setSnapshot(DEFAULTS);
        }}
        destructive
        confirmLabel={t('cdn_domain.cache_tab.action_remove', 'Remove')}
      />
    </Section>
  );
}
